import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, setAuthToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Shield } from "lucide-react";

function formatErr(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || JSON.stringify(d);
}

export default function AdminSignup() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    admin_code: params.get("code") || "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const nav = useNavigate();
  const { refresh } = useAuth();

  if (pending) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100" style={{ fontFamily: "Manrope, sans-serif" }}>
        <div className="mx-auto max-w-md px-6 py-16" data-testid="admin-signup-check-inbox">
          <Link to="/" className="text-2xl font-extrabold text-[#F59E0B]">Clinq</Link>
          <h1 className="mt-8 text-3xl font-bold">Check your inbox</h1>
          <p className="mt-3 text-sm text-neutral-300">We sent a verification link to <span className="font-semibold text-white">{pending}</span>. Click it to activate your admin account and sign in.</p>
          <p className="mt-2 text-xs text-neutral-500">The link expires in 24 hours.</p>
          <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-[#F59E0B] underline">Go to sign in</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/signup", { ...form, role: "admin" });
      if (data?.verification_required) { setErr(""); setPending(data.email); return; }
      if (data?.token) setAuthToken(data.token);
      await refresh();
      nav("/admin/dashboard");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mx-auto max-w-md px-6 py-16">
        <Link to="/" className="text-2xl font-extrabold text-[#F59E0B]" data-testid="admin-signup-logo">Clinq</Link>
        <div className="mt-8 flex items-center gap-2">
          <Shield className="h-5 w-5 text-[#F59E0B]"/>
          <h1 className="text-3xl font-bold">Create admin account</h1>
        </div>
        <p className="mt-2 text-sm text-neutral-400">
          Restricted access — requires a Clinq admin signup code.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <input data-testid="admin-signup-name" placeholder="Full name" required value={form.name}
            onChange={e => setForm({...form, name: e.target.value})}
            className="w-full rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3 text-sm outline-none focus:border-[#F59E0B]" />
          <input data-testid="admin-signup-email" type="email" placeholder="Email" required value={form.email}
            onChange={e => setForm({...form, email: e.target.value})}
            className="w-full rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3 text-sm outline-none focus:border-[#F59E0B]" />
          <input data-testid="admin-signup-password" type="password" placeholder="Password (min 6 chars)" required minLength={6} value={form.password}
            onChange={e => setForm({...form, password: e.target.value})}
            className="w-full rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3 text-sm outline-none focus:border-[#F59E0B]" />
          <input data-testid="admin-signup-code" placeholder="Admin signup code" required value={form.admin_code}
            onChange={e => setForm({...form, admin_code: e.target.value})}
            className="w-full rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3 text-sm outline-none focus:border-[#F59E0B] font-mono" />
          {err && <p className="text-sm text-red-400" data-testid="admin-signup-error">{err}</p>}
          <button data-testid="admin-signup-submit" disabled={busy} type="submit"
            className="w-full rounded-lg bg-[#F59E0B] py-3 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
            {busy ? "Creating…" : "Create admin account"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-neutral-400">
          Already an admin?{" "}
          <Link to="/login" className="font-semibold text-[#F59E0B] hover:underline" data-testid="admin-signup-to-login">Log in</Link>
        </p>
      </div>
    </div>
  );
}
